import { usePreventNavigation } from '@monorepo/shared/hooks/navigation/usePreventNavigation';
import { useCallbackWithConditionalConfirmation } from '@monorepo/shared/hooks/useCallbackWithConditionalConfirmation';
import { useCallback, useEffect, useState } from 'react';

type UseCloseWithConfirmationProps = {
  requiresConfirmation: boolean;
  unsavedWorkMessage: string;
  onClose: () => void;
};

export function useCloseWithConfirmation({
  requiresConfirmation,
  unsavedWorkMessage,
  onClose,
}: UseCloseWithConfirmationProps) {
  const confirmationTitle = 'Close Editor';
  const confirmationDescription = `Are you sure you want to close? ${unsavedWorkMessage}`;
  const [isClosing, setIsClosing] = useState(false);

  usePreventNavigation(requiresConfirmation && !isClosing, {
    title: confirmationTitle,
    description: confirmationDescription,
  });

  // Defers onClose a render so usePreventNavigation has released its block;
  // otherwise a consumer's onClose that navigates would prompt again
  useEffect(() => {
    if (!isClosing) return;
    onClose();
    setIsClosing(false);
  }, [isClosing, onClose]);

  const closeWithoutConfirmation = useCallback(() => setIsClosing(true), []);

  const onCloseWithConfirmation = useCallbackWithConditionalConfirmation(
    requiresConfirmation,
    {
      title: confirmationTitle,
      description: confirmationDescription,
      onConfirmCallback: closeWithoutConfirmation,
    },
  );

  return {
    closeWithoutConfirmation,
    onCloseWithConfirmation,
  };
}
